import axios, { AxiosError, AxiosHeaders } from 'axios';

const isProductionBuild = process.env.NODE_ENV === 'production';
const baseURL = isProductionBuild
  ? process.env.REACT_APP_API_BASE_URL_PROD ?? '/'
  : process.env.REACT_APP_API_BASE_URL_TEST ?? '';

let hasRedirectedAfter401 = false;
let activeTenantHeaderId: string | null = null;
let tenantRequestController = new AbortController();
export const SESSION_UNAUTHORIZED_EVENT = 'billingservice:session-unauthorized';

const API_ERROR_MESSAGES: Record<string, string> = {
  'Not Found': 'Страница или ресурс не найден.',
  'Request failed with status code 400': 'Некорректный запрос.',
  'Request failed with status code 401': 'Необходимо войти в систему.',
  'Request failed with status code 403': 'Недостаточно прав.',
  'Request failed with status code 404': 'Страница или ресурс не найден.',
  'Request failed with status code 413': 'Файл слишком большой.',
  'Request failed with status code 422': 'Проверьте заполненные данные.',
  'Request failed with status code 500': 'Внутренняя ошибка сервера.',
  'Request failed with status code 502': 'Сервис временно недоступен.',
  'Request failed with status code 503': 'Сервис временно недоступен.',
  'Network Error': 'Нет связи с сервером.',
  Unauthorized: 'Необходимо войти в систему.',
  Forbidden: 'Недостаточно прав.',
  'Bad Request': 'Некорректный запрос.',
  'Internal Server Error': 'Внутренняя ошибка сервера.',
  'Missing access token': 'Необходимо войти в систему.',
  'Could not validate credentials': 'Сессия истекла. Войдите в систему заново.',
  'Not enough permissions': 'Недостаточно прав.',
  'Not enough permissions for tickets': 'Недостаточно прав для работы с заявками.',
  'Superadmin privileges required': 'Требуются права суперадминистратора.',
  'Tenant schema not found': 'Не удалось определить клиента.',
};

export const translateApiErrorMessage = (message?: unknown, status?: number) => {
  if (typeof message === 'string' && message.trim()) {
    const trimmed = message.trim();
    if (API_ERROR_MESSAGES[trimmed]) {
      return API_ERROR_MESSAGES[trimmed];
    }
  }
  if (status === 400) return 'Некорректный запрос.';
  if (status === 401) return 'Необходимо войти в систему.';
  if (status === 403) return 'Недостаточно прав.';
  if (status === 404) return 'Страница или ресурс не найден.';
  if (status === 413) return 'Файл слишком большой.';
  if (status === 422) return 'Проверьте заполненные данные.';
  if (status === 502 || status === 503 || status === 504) return 'Сервис временно недоступен.';
  if (status && status >= 500) return 'Внутренняя ошибка сервера.';
  return typeof message === 'string' ? message : '';
};

const normalizeApiError = (error: AxiosError) => {
  const status = error.response?.status;
  const responseData = error.response?.data;
  const detail =
    responseData && typeof responseData === 'object' && 'detail' in responseData
      ? (responseData as { detail?: unknown }).detail
      : undefined;
  const translatedDetail = translateApiErrorMessage(detail, status);
  const translatedMessage = translateApiErrorMessage(error.message, status);
  const message = translatedDetail || translatedMessage;

  if (message && error.response) {
    if (responseData && typeof responseData === 'object') {
      (responseData as { detail?: string }).detail = message;
    } else {
      error.response.data = { detail: message } as typeof error.response.data;
    }
  }

  if (message) {
    Object.defineProperty(error, 'message', {
      value: message,
      configurable: true,
    });
  }
};

const redirectToLogin = () => {
  if (hasRedirectedAfter401 || typeof window === 'undefined') {
    return;
  }

  hasRedirectedAfter401 = true;
  window.dispatchEvent(new Event(SESSION_UNAUTHORIZED_EVENT));

  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

export const resetUnauthorizedRedirectState = () => {
  hasRedirectedAfter401 = false;
};

export const setTenantHeader = (tenantId: string | null) => {
  const normalizedTenantId = tenantId || null;

  if (activeTenantHeaderId !== normalizedTenantId) {
    tenantRequestController.abort();
    tenantRequestController = new AbortController();
    activeTenantHeaderId = normalizedTenantId;
  }

  if (normalizedTenantId) {
    apiClient.defaults.headers.common['X-Tenant-Id'] = normalizedTenantId;
  } else {
    delete apiClient.defaults.headers.common['X-Tenant-Id'];
  }
};

const warnUnauthorized = (error: AxiosError) => {
  const status = error.response?.status;
  const requestUrl = error.config?.url ?? '[unknown-url]';
  console.warn(
    `[api] Request failed (${requestUrl}) with status ${status ?? 'no-response'}:`,
    error.message
  );
};

const shouldForceRedirect = (error: AxiosError) => {
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
    return false;
  }

  if (error.response?.status === 401) {
    return true;
  }

  return false;
};

const apiClient = axios.create({
  //baseURL: '/api',
  baseURL, 
  withCredentials: true, 
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  if (!config.signal) {
    config.signal = tenantRequestController.signal;
  }

  if (typeof window !== 'undefined') {
    const activeTenantId = window.localStorage.getItem('activeTenantId');
    if (activeTenantId) {
      const headers = config.headers instanceof AxiosHeaders
        ? config.headers
        : AxiosHeaders.from(config.headers || {});
      headers.set('X-Tenant-Id', activeTenantId);
      config.headers = headers;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    normalizeApiError(error);
    if (shouldForceRedirect(error)) {
      warnUnauthorized(error);
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export default apiClient;
